import React, { useLayoutEffect, useRef } from 'react';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

/** Nejmenší písmo na kiosku: čitelnost ze 3–5 m. Pod tuto mez se nezmenšuje. */
export const KIOSK_MIN_FONT_PX = 16;

interface FitHeightBoxProps {
    /** Výchozí (největší) písmo v px. */
    maxFontPx?: number;
    /** Výchozí písmo jako CSS délka (např. 'var(--dashboard-info-size)'); má přednost před maxFontPx. */
    maxFont?: string;
    /** Nejmenší písmo v px (výchozí KIOSK_MIN_FONT_PX). */
    minFontPx?: number;
    // Změna obsahu, při které se velikost písma přepočítá.
    fitKey?: string;
    /**
     * Jen pro box bez flex/grid rozložení (prostý text nebo blokové odstavce): když se obsah
     * nevejde ani při minFontPx, ořízne se na celé řádky s „…" na konci posledního viditelného
     * řádku (-webkit-line-clamp). Bez toho se „…" ukáže v rohu boxu.
     */
    orezRadky?: boolean;
    sx?: SxProps<Theme>;
    children: React.ReactNode;
    'data-testid'?: string;
}

const pretika = (el: HTMLElement) =>
    el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1;

// Počet řádků textu, které jsou v boxu vidět celé (spodní hrana nad spodním paddingem).
// Počítá se z polohy řádků, ne z výšky obsahu: s -webkit-line-clamp se skryté řádky dál
// započítávají do scrollHeight, takže měření přes přetečení by vedlo vždy k jednomu řádku.
const celeRadky = (el: HTMLElement, sizePx: number): { radky: number; vyska?: number } => {
    const cs = getComputedStyle(el);
    const box = el.getBoundingClientRect();
    // Měřítko vykreslení. V náhledu na výšku je kiosek zmenšený přes transform: scale:
    // getBoundingClientRect a Range.getClientRects vracejí px PO transformaci, ale maxHeight,
    // písmo a padding jsou v nezmenšených CSS px. Na ostrém kiosku (a v jsdom) je m = 1.
    // Rozdíl do 1 px je zaokrouhlení offsetHeight na celé px, ne transformace: m = 1,
    // aby se monitor na šířku choval bit po bitu jako dřív (platí i pro boxy nižší než 50 px).
    const m = el.offsetHeight > 0 && box.height > 0 && Math.abs(box.height - el.offsetHeight) > 1
        ? box.height / el.offsetHeight
        : 1;
    const spodek = box.bottom
        - ((parseFloat(cs.paddingBottom) || 0) + (parseFloat(cs.borderBottomWidth) || 0)) * m;
    const range = document.createRange?.();
    if (range && typeof range.getClientRects === 'function') {
        range.selectNodeContents(el);
        const spodkyRadku: number[] = [];
        Array.from(range.getClientRects()).forEach(r => {
            if (r.height <= 0 || r.bottom > spodek + 0.5 * m) return;
            if (!spodkyRadku.some(b => Math.abs(b - r.bottom) < (sizePx * m) / 2)) spodkyRadku.push(r.bottom);
        });
        if (spodkyRadku.length > 0) {
            // Výška boxu (border-box) končící přesně spodkem posledního celého řádku. Spodní padding
            // se při ořezu ruší: overflow:hidden ořezává až na hraně paddingu, takže by v něm
            // prosvítal horní kus dalšího (skrytého) řádku. Převod do CSS px dělením m.
            const posledni = Math.max(...spodkyRadku);
            return { radky: spodkyRadku.length, vyska: Math.floor((posledni - box.top) / m) };
        }
        // Range umí měřit, ale žádný řádek se do boxu celý nevejde.
        if (box.height > 0) return { radky: 0 };
    }
    // Bez měření řádků (Range.getClientRects chybí): odhad z vnitřní výšky boxu.
    const vyska = el.clientHeight - (parseFloat(cs.paddingTop) || 0) - (parseFloat(cs.paddingBottom) || 0);
    return { radky: Math.max(0, Math.floor(vyska / (sizePx * 1.3))) };
};

// Box, který si sám zmenší písmo tak, aby se jeho obsah vešel do výšky i šířky, kterou mu dá
// rodič (flex-shrink / maxHeight / grid). Kiosek se nedá posouvat ani najet myší, takže text
// se nesmí potichu ořezávat: radši menší písmo, dokud se nevejde. Potomci mají velikosti v em,
// aby se zmenšovali s boxem. Když se obsah nevejde ani při minFontPx, box dostane
// data-orezano="true" a vpravo dole se ukáže „…" jako náznak, že text pokračuje.
export const FitHeightBox: React.FC<FitHeightBoxProps> = ({
    maxFontPx, maxFont, minFontPx = KIOSK_MIN_FONT_PX, fitKey, orezRadky = false, sx, children, 'data-testid': testId,
}) => {
    const ref = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        const fit = () => {
            if (orezRadky) {
                el.style.display = '';
                el.style.maxHeight = '';
                el.style.paddingBottom = '';
                el.style.boxSizing = '';
                el.style.removeProperty('-webkit-line-clamp');
                el.style.removeProperty('-webkit-box-orient');
            }
            let size: number;
            if (maxFont) {
                el.style.fontSize = maxFont;
                size = Math.floor(parseFloat(getComputedStyle(el).fontSize)) || maxFontPx || minFontPx;
            } else {
                size = maxFontPx ?? minFontPx;
            }
            size = Math.max(size, minFontPx);
            el.style.fontSize = `${size}px`;
            while (size > minFontPx && pretika(el)) {
                size -= 1;
                el.style.fontSize = `${size}px`;
            }
            el.removeAttribute('data-radky');
            if (!pretika(el)) {
                el.removeAttribute('data-orezano');
                return;
            }
            if (orezRadky) {
                // Nejvíc celých řádků, které se do boxu vejdou; poslední končí „…".
                // Box se zároveň zkrátí na spodek posledního celého řádku: jinak by pod řádkem
                // s „…" prosvítal napůl uříznutý další řádek (box má výšku od flexu, ne od clampu).
                const { radky, vyska } = celeRadky(el, size);
                if (radky < 1) {
                    // Nevejde se ani jeden celý řádek: clamp by „…" schoval pod hranu boxu,
                    // proto rohový náznak „…" jako u boxu bez orezRadky.
                    el.setAttribute('data-orezano', 'true');
                    return;
                }
                el.setAttribute('data-orezano', 'radky');
                el.style.display = '-webkit-box';
                el.style.setProperty('-webkit-box-orient', 'vertical');
                el.style.setProperty('-webkit-line-clamp', String(radky));
                el.setAttribute('data-radky', String(radky));
                if (vyska) {
                    el.style.boxSizing = 'border-box';
                    el.style.paddingBottom = '0px';
                    el.style.maxHeight = `${vyska}px`;
                }
            } else {
                el.setAttribute('data-orezano', 'true');
            }
        };
        fit();
        if (typeof ResizeObserver === 'undefined') return;
        // Přepočet až v dalším snímku: fit() mění velikost sledovaných prvků a dva boxy ve stejném
        // sloupci (detaily + zprávy bez mapy) by si jinak v jednom průchodu přeposílaly notifikace
        // („ResizeObserver loop completed with undelivered notifications").
        let snimek = 0;
        const observer = new ResizeObserver(() => {
            cancelAnimationFrame(snimek);
            snimek = requestAnimationFrame(fit);
        });
        observer.observe(el);
        if (el.parentElement) observer.observe(el.parentElement);
        return () => { observer.disconnect(); cancelAnimationFrame(snimek); };
    }, [maxFontPx, maxFont, minFontPx, fitKey, orezRadky]);

    return (
        <Box ref={ref} data-testid={testId} sx={{
            overflow: 'hidden', minWidth: 0, position: 'relative',
            // S orezRadky aspoň jeden čitelný řádek při minFontPx: flex rodiče box nesmí zmáčknout
            // pod výšku řádku, jinak by obsah (i náznak „…") tiše zmizel.
            minHeight: orezRadky ? `${minFontPx * 1.3}px` : 0,
            '&[data-orezano="true"]::after': {
                content: '"…"', position: 'absolute', right: 0, bottom: 0,
                px: 0.75, fontWeight: 900, lineHeight: 1.2,
                backgroundColor: 'inherit', color: 'inherit',
            },
            ...(sx as object),
        }}>
            {children}
        </Box>
    );
};
